import React from "react";
import { ArrowLeft } from "lucide-react";
import { finApi, money } from "@/lib/financeApi";
import { Chip, ErrorBox, Loading, PAYOUT_CHIP, useLoad, when } from "@/components/nds/windows/finance/parts";
import { DocAttach, HistoryList, RefLedger } from "@/components/nds/windows/finance/recordParts";
import PayoutActions from "@/components/nds/windows/finance/PayoutActions";
import { CardDeck, CardsBack, useCardTabs } from "@/components/nds/TabCards";

function DetailsTab({ p, reload }) {
  const F = ({ l, v, t }) => <div className="space-y-0.5"><div className="font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">{l}</div><div data-testid={t} className="text-sm text-nex-silver">{v || "—"}</div></div>;
  return (
    <div data-testid="fin-payout-details-tab" className="p-5 space-y-4">
      <PayoutActions p={p} onChanged={reload} />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <F l="Staff" v={p.staff_name} />
        <F l="Session date" v={p.session_date} />
        <F l="Session type" v={p.session_type.replace(/_/g, " ")} />
        <F l="Hours × rate" v={`${p.hours} × ${money(p.rate_per_hour)}`} />
        <F l="Gross" v={money(p.gross_amount)} t="fin-payout-detail-gross" />
        <F l="Residents" v={`${p.resident_ids.length} in session`} />
        <F l="Created" v={`${p.created_by?.name || "—"} · ${when(p.created_at)}`} />
        <F l="Approved" v={p.approved_at && `${p.approved_by?.name} · ${when(p.approved_at)}`} />
        <F l="Paid" v={p.paid_at && `${p.paid_by?.name} · ${when(p.paid_at)}`} />
        {p.void_reason && <F l="Void reason" v={p.void_reason} t="fin-payout-detail-void-reason" />}
        <F l="Notes" v={p.notes} />
      </div>
      <DocAttach prefix="/session-payouts" rec={p} onChanged={reload} testid="fin-payout" />
    </div>
  );
}

const TABS = [
  ["details", "Details", DetailsTab],
  ["history", "History", ({ p }) => <HistoryList history={p.history} chips={PAYOUT_CHIP} testid="fin-payout-history" />],
  ["ledger", "Ledger", ({ p }) => <RefLedger refType="session_payout" refId={p.id} testid="fin-payout-ledger" deps={[p.status]} />],
];

export default function FinPayoutDetail({ payoutId, onBack }) {
  const ct = useCardTabs("details");
  const tab = ct.tab;
  const setTab = ct.open;
  const [state, load] = useLoad(() => finApi.get(`/session-payouts/${payoutId}`), [payoutId]);
  const back = <button type="button" onClick={onBack} data-testid="fin-payout-back" className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold"><ArrowLeft className="h-3 w-3" /> All payouts</button>;
  if (state.loading && !state.data) return <div className="p-5">{back}<Loading testid="fin-payout-detail-loading" /></div>;
  if (state.error) return <div className="p-5">{back}<ErrorBox testid="fin-payout-detail-error" error={state.error} onRetry={load} /></div>;
  const p = state.data;
  const [tone, l] = PAYOUT_CHIP[p.status];
  const Tab = TABS.find((t) => t[0] === tab)[2];
  const cards = [
    { id: "details", label: "Details", tone: "financial", desc: "Amount, staff member, session and actions", stat: money(p.gross_amount || 0), note: l },
    { id: "history", label: "History", tone: "system", desc: "Every status change on this payout", stat: (p.history || []).length, note: (p.history || []).length === 1 ? "event" : "events" },
    { id: "ledger", label: "Ledger", tone: "data", desc: "Ledger lines posted by this payout" },
  ];
  return (
    <div data-testid="fin-payout-detail" className="flex flex-col h-full">
      <div className="px-5 pt-4 space-y-3 border-b border-white/10">
        {back}
        <div className="flex items-center gap-3"><h3 className="text-lg text-nex-silver font-semibold">{p.staff_name} · {p.session_date}</h3><Chip tone={tone} data-testid="fin-payout-detail-status">{l}</Chip></div>
        {ct.compact ? <div className="pb-3" /> : <div className="flex gap-4">
          {TABS.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)} data-testid={`fin-payout-tab-${id}`} aria-pressed={tab === id}
              className={`pb-2 font-mono text-[10px] tracking-[0.2em] uppercase border-b-2 transition-colors ${tab === id ? "border-nex-gold text-nex-gold" : "border-transparent text-nex-muted hover:text-nex-silver"}`}>{label}</button>
          ))}
        </div>}
      </div>
      <div ref={ct.ref} className="flex-1 overflow-y-auto">
        {ct.deck ? <div className="p-5"><CardDeck testid="fin-payout-cards" items={cards} onOpen={setTab} /></div>
          : <>{ct.compact && <CardsBack testid="fin-payout-cards-back" className="mx-5 mt-4" label={TABS.find((t) => t[0] === tab)[1]} onClick={ct.close} />}<Tab p={p} reload={load} /></>}
      </div>
    </div>
  );
}
